import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, X, Save, Loader2, CheckCircle2, AlertCircle,
  AlertTriangle, Calendar, Users, GraduationCap, MapPin,
  Building2, UserSquare, Crown, KeyRound, Lock, Pencil,
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { organizationService, authService } from '../../services';

/**
 * Edit Organization Page
 * Form to edit an existing organization in the system
 */

// Shared chrome — kept in lockstep with AddOrganizationPage so the
// two forms read as a single design system.
const CARD_BASE =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)]';
const CARD_FORM =
  'rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.08)] overflow-hidden';

const PREVIEW_AVATAR_GRADIENTS = [
  'from-primary-500 to-primary-700',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-600',
  'from-cyan-500 to-sky-600',
];
const seededPreviewGradient = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) & 0x7fffffff;
  return PREVIEW_AVATAR_GRADIENTS[hash % PREVIEW_AVATAR_GRADIENTS.length];
};

// Plan data — same shape as AddOrganizationPage so the picker stays
// identical across create/edit.
const PLANS = [
  { id: 'free',                name: 'Free Plan',          price: '₹0',     period: 'forever', desc: '3 sessions (one-time), 30 participants', features: ['3 sessions', '30 participants', '1 trainer'] },
  { id: 'pro',                 name: 'Pro Plan',           price: '₹1,999', period: '/month',  desc: '20 sessions/month, 30 participants, 1 trainer', features: ['20 sessions/mo', '30 participants', '1 trainer'] },
  { id: 'pro_plus',            name: 'Pro Plus',           price: '₹5,999', period: '/month',  desc: 'Unlimited sessions, 100 participants, 3 trainers', features: ['Unlimited', '100 participants', '3 trainers'], popular: true },
  { id: 'enterprise_starter',  name: 'Enterprise Starter', price: '₹1.8L',  period: '/year',   desc: 'Unlimited sessions, 200 participants, 5 trainers', features: ['Unlimited', '200 participants', '5 trainers'] },
  { id: 'enterprise_scale',    name: 'Enterprise Scale',   price: 'Custom', period: '',        desc: 'Custom limits, unlimited trainers, dedicated support', features: ['Unlimited', 'Unlimited participants', 'Custom'] },
];

const PLAN_PILL = {
  free:               'bg-neutral-50 text-neutral-700 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700',
  pro:                'bg-primary-50 text-primary-700 ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30',
  pro_plus:           'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/30',
  enterprise_starter: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30',
  enterprise_scale:   'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30',
};

const FIELD_LABELS = {
  name: 'Organization name',
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone number',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

// Inline date helpers — no library.
const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};
const formatRelative = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMs / 3600000);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffDay <= 14) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  return formatDate(d);
};

const EditOrganizationPage = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);
  const [originalRecord, setOriginalRecord] = useState(null);
  const [emailUnlocked, setEmailUnlocked] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Multiplexed danger-zone confirm dialog. Reuses the existing
  // ConfirmDialog component so we don't reinvent the modal flow.
  const [dangerDialog, setDangerDialog] = useState({ isOpen: false, title: '', message: '', confirmText: '', variant: 'danger', onConfirm: () => {} });
  const closeDangerDialog = () => setDangerDialog((d) => ({ ...d, isOpen: false }));

  const [formData, setFormData] = useState({
    name: '',
    website: '',
    description: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    plan: 'free',
    status: 'active',
  });

  const [errors, setErrors] = useState({});

  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const initialFormDataRef = useRef(null);

  const checkIfDirty = () => {
    if (!initialFormDataRef.current) return false;
    return JSON.stringify(formData) !== initialFormDataRef.current;
  };

  const handleBackNavigation = (path) => {
    if (checkIfDirty()) {
      setPendingNavigation(path);
      setShowBackConfirm(true);
    } else {
      navigate(path);
    }
  };

  const confirmLeave = () => {
    setShowBackConfirm(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  useEffect(() => {
    const loadOrganization = async () => {
      try {
        setIsLoading(true);
        const response = await organizationService.getOrganization(organizationId);
        const org = response.data || response;
        setOriginalRecord(org);

        const addressData = org.address || {};
        const contactPerson = org.contactPerson || org.contactName || '';
        const nameParts = contactPerson.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        const loadedData = {
          name: org.name || '',
          website: org.website || '',
          description: org.description || '',
          street: addressData.street || '',
          city: addressData.city || '',
          state: addressData.state || '',
          zipCode: addressData.zipCode || '',
          country: addressData.country || '',
          firstName: firstName,
          lastName: lastName,
          email: org.email || org.contactEmail || '',
          phone: org.phone || '',
          jobTitle: org.jobTitle || '',
          plan: org.plan || 'free',
          status: org.status || 'active',
        };
        setFormData(loadedData);
        initialFormDataRef.current = JSON.stringify(loadedData);
      } catch (error) {
        toast.error('Failed to load organization');
        console.error('Error loading organization:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadOrganization();
  }, [organizationId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Organization name is required';
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (formData.phone && !/^\+?[\d\s\-()]+$/.test(formData.phone)) newErrors.phone = 'Invalid phone number format';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        name: formData.name,
        website: formData.website || undefined,
        description: formData.description || undefined,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
        },
        contactPerson: `${formData.firstName} ${formData.lastName}`.trim(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        jobTitle: formData.jobTitle || undefined,
        plan: formData.plan,
        status: formData.status,
      };

      await organizationService.updateOrganization(organizationId, submitData);
      toast.success('Organization updated successfully!');
      navigate(`/admin/organizations/${organizationId}`);
    } catch (error) {
      toast.error(error.message || 'Failed to update organization');
      console.error('Error updating organization:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    handleBackNavigation(`/admin/organizations/${organizationId}`);
  };

  // ⌘+Enter / Ctrl+Enter submit shortcut.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isSubmitting && checkIfDirty()) {
        e.preventDefault();
        formRef.current?.requestSubmit?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, formData]);

  // ────── Edit-only handlers ──────
  const handleSendPasswordReset = async () => {
    if (!formData.email) {
      toast.error('No email on file for the contact');
      return;
    }
    setIsResettingPassword(true);
    try {
      await authService.forgotPassword(formData.email);
      toast.success('Password reset link sent.');
    } catch (error) {
      toast.error(error.message || 'Failed to send reset link');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const isSuspended = formData.status === 'suspended';

  const handleToggleSuspend = () => {
    if (isSuspended) {
      setDangerDialog({
        isOpen: true,
        title: 'Reactivate organization',
        message: `Restore access for all users in ${formData.name || 'this organization'}? They will be able to log in immediately.`,
        confirmText: 'Reactivate',
        variant: 'success',
        onConfirm: async () => {
          try {
            await organizationService.reactivateOrganization(organizationId);
            toast.success(`${formData.name || 'Organization'} has been reactivated`);
            setFormData((prev) => ({ ...prev, status: 'active' }));
            // Re-baseline so the status flip doesn't read as "dirty" form.
            initialFormDataRef.current = JSON.stringify({ ...formData, status: 'active' });
          } catch (error) {
            toast.error(error.message || 'Failed to reactivate');
          }
        },
      });
    } else {
      setDangerDialog({
        isOpen: true,
        title: 'Suspend organization',
        message: `Temporarily block all users in ${formData.name || 'this organization'} from logging in. You can reactivate them later.`,
        confirmText: 'Suspend',
        variant: 'danger',
        onConfirm: async () => {
          try {
            // organizationService.suspendOrganization expects (id, reason).
            // We pass an empty reason here; the org list page exposes the
            // full reason-modal flow if a reason is required.
            await organizationService.suspendOrganization(organizationId, 'Suspended from edit page');
            toast.success(`${formData.name || 'Organization'} has been suspended`);
            setFormData((prev) => ({ ...prev, status: 'suspended' }));
            initialFormDataRef.current = JSON.stringify({ ...formData, status: 'suspended' });
          } catch (error) {
            toast.error(error.message || 'Failed to suspend');
          }
        },
      });
    }
  };

  const handleDelete = () => {
    setDangerDialog({
      isOpen: true,
      title: 'Delete organization',
      message: `Permanently remove ${formData.name || 'this organization'} and all of its sessions, trainers, and participants. This cannot be undone.`,
      confirmText: 'Delete organization',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await organizationService.deleteOrganization(organizationId);
          toast.success(`${formData.name || 'Organization'} has been deleted`);
          // Bypass the dirty-check on navigation since the record no
          // longer exists.
          initialFormDataRef.current = JSON.stringify(formData);
          navigate('/admin/organizations');
        } catch (error) {
          toast.error(error.message || 'Failed to delete');
        }
      },
    });
  };

  // ────── Derived render values ──────
  const isDirty = checkIfDirty();
  const previewInitial = (formData.name || '').charAt(0).toUpperCase();
  const previewGradient = seededPreviewGradient(previewInitial || 'A');
  const selectedPlan = PLANS.find((p) => p.id === formData.plan) || PLANS[0];
  const planPillCls = PLAN_PILL[formData.plan] || PLAN_PILL.free;
  const errorKeys = Object.keys(errors).filter((k) => !!errors[k]);
  const errorLabelList = errorKeys.map((k) => FIELD_LABELS[k] || k).join(', ');

  // Activity rows — only render the card if at least one is present.
  const activityRows = [
    originalRecord?.lastSessionAt && { label: 'Last session run', date: originalRecord.lastSessionAt },
    originalRecord?.subscriptionRenewedAt && { label: 'Subscription renewed', date: originalRecord.subscriptionRenewedAt },
    originalRecord?.lastInvoiceAt && { label: 'Last invoice paid', date: originalRecord.lastInvoiceAt },
  ].filter(Boolean).slice(0, 3);

  return (
    <DashboardLayout role="super-admin">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)] gap-6 w-full px-4 lg:px-6 py-6">
        {/* ────── LEFT RAIL ────── */}
        <aside className="lg:sticky lg:top-6 lg:self-start space-y-4">
          {/* A. Page identity */}
          <div className={`${CARD_BASE} p-5`}>
            <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center bg-amber-50 ring-1 ring-inset ring-amber-100 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20">
              <Building2 size={18} strokeWidth={1.75} />
            </span>
            <div className="flex items-center mt-3 flex-wrap gap-1">
              <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">Edit organization</h2>
              {isDirty && (
                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Unsaved
                </span>
              )}
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Update organization details, contact, and subscription.</p>

            {/* Metadata stack */}
            {isLoading ? (
              <div className="mt-4 space-y-1.5">
                <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
                <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
                <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
              </div>
            ) : (
              <dl className="text-xs text-neutral-500 dark:text-neutral-400 mt-4 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <dt>Created</dt>
                  <dd className="tabular-nums text-neutral-700 dark:text-neutral-200">{formatDate(originalRecord?.createdAt)}</dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt>Last updated</dt>
                  <dd className="tabular-nums text-neutral-700 dark:text-neutral-200">{formatRelative(originalRecord?.updatedAt)}</dd>
                </div>
              </dl>
            )}

            <button
              type="button"
              onClick={() => handleBackNavigation('/admin/organizations')}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 inline-flex items-center gap-1 mt-4 transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.75} />
              Back to Organizations
            </button>
          </div>

          {/* B. Live preview */}
          <div className={`${CARD_BASE} overflow-hidden`}>
            <div className="bg-gradient-to-r from-neutral-50 to-white dark:from-neutral-900 dark:to-neutral-900/60 px-4 py-2 text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-400 border-b border-neutral-100 dark:border-neutral-800/70">
              Preview
            </div>
            <div className="p-4 space-y-3">
              {isLoading ? (
                <>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-neutral-200 dark:bg-neutral-800 animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse w-3/4" />
                      <div className="h-3 rounded bg-neutral-200 dark:bg-neutral-800 animate-pulse w-1/2" />
                    </div>
                  </div>
                  <div className="h-4 rounded-full bg-neutral-200 dark:bg-neutral-800 animate-pulse w-20" />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    {previewInitial ? (
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${previewGradient} flex items-center justify-center text-white font-semibold shadow-sm`}>
                        {previewInitial}
                      </div>
                    ) : (
                      <div className="w-11 h-11 rounded-xl border-2 border-dashed border-neutral-200 dark:border-neutral-700 flex items-center justify-center text-neutral-300 dark:text-neutral-600">
                        <Building2 size={18} strokeWidth={1.75} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${formData.name ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-400'}`}>
                        {formData.name || 'Organization name'}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{formData.email || '—'}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${planPillCls}`}>
                    {selectedPlan.name}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* C. Recent activity (only if data present + not loading) */}
          {!isLoading && activityRows.length > 0 && (
            <div className={`${CARD_BASE} p-4`}>
              <p className="text-[11px] uppercase tracking-[0.06em] font-semibold text-neutral-400 mb-2">Recent activity</p>
              {activityRows.map((row) => (
                <div key={row.label} className="flex items-center gap-2 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-neutral-300 dark:bg-neutral-600" />
                  <span className="text-sm text-neutral-700 dark:text-neutral-200">{row.label}</span>
                  <span className="ml-auto text-xs text-neutral-400 dark:text-neutral-500 tabular-nums">{formatRelative(row.date)}</span>
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* ────── MAIN FORM ────── */}
        <main>
          <form ref={formRef} onSubmit={handleSubmit} className={CARD_FORM}>
            {/* Hero strip */}
            <div className="h-14 bg-gradient-to-r from-primary-50 via-white to-white dark:from-primary-500/5 dark:via-neutral-900 dark:to-neutral-900 border-b border-neutral-100 dark:border-neutral-800/70 px-6 flex items-center gap-3">
              <span className="inline-flex w-8 h-8 rounded-lg items-center justify-center bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100 dark:bg-primary-500/10 dark:text-primary-400 dark:ring-primary-500/20">
                <Building2 size={16} strokeWidth={1.75} />
              </span>
              <h1 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Edit organization</h1>
            </div>

            <div className="p-6 lg:p-8 space-y-8">
              {isLoading ? (
                <SkeletonForm />
              ) : (
                <>
                  {/* Error summary */}
                  {errorKeys.length > 0 && (
                    <div className="rounded-xl bg-rose-50 ring-1 ring-inset ring-rose-200 px-4 py-3 flex items-start gap-3 dark:bg-rose-500/10 dark:ring-rose-500/20">
                      <AlertCircle size={18} strokeWidth={1.75} className="text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-rose-900 dark:text-rose-200">Please fix the errors below</p>
                        <p className="text-xs text-rose-700/80 dark:text-rose-300/80 mt-0.5">{errorLabelList}</p>
                      </div>
                    </div>
                  )}

                  {/* ── Section 1 ── */}
                  <section>
                    <SectionHeader Icon={Building2} title="Organization details" subtitle="Public information about the company." />
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      <div className="md:col-span-2 xl:col-span-3">
                        <Input label="Organization Name" name="name" value={formData.name} onChange={handleChange} placeholder="ABC Corporation" required fullWidth size="sm" error={errors.name} />
                      </div>

                      <Input label="Website" name="website" type="url" value={formData.website} onChange={handleChange} placeholder="https://www.example.com" fullWidth size="sm" />

                      <div className="md:col-span-2 xl:col-span-3">
                        <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">
                          Description <span className="text-neutral-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          rows={2}
                          className="w-full px-3 py-2 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition resize-none"
                          placeholder="Brief description of the organization"
                        />
                      </div>

                      {/* Address sub-group */}
                      <div className="md:col-span-2 xl:col-span-3">
                        <div className="flex items-center gap-1.5 mb-2 text-xs font-medium text-neutral-700 dark:text-neutral-200">
                          <MapPin size={14} strokeWidth={1.75} className="text-neutral-400" />
                          Address
                          <span className="text-neutral-400 font-normal">(optional)</span>
                        </div>
                        <div className="rounded-xl bg-neutral-50/60 dark:bg-neutral-900/40 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 p-4 space-y-3">
                          <Input name="street" value={formData.street} onChange={handleChange} placeholder="Street Address" fullWidth size="sm" />
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            <Input name="city" value={formData.city} onChange={handleChange} placeholder="City" fullWidth size="sm" />
                            <Input name="state" value={formData.state} onChange={handleChange} placeholder="State/Province" fullWidth size="sm" />
                            <Input name="zipCode" value={formData.zipCode} onChange={handleChange} placeholder="ZIP/Postal Code" fullWidth size="sm" />
                            <Input name="country" value={formData.country} onChange={handleChange} placeholder="Country" fullWidth size="sm" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* ── Section 2 ── */}
                  <section>
                    <SectionHeader
                      Icon={UserSquare}
                      title="Primary contact"
                      subtitle="This person manages the organization and receives system mail."
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      <Input label="First Name" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" required fullWidth size="sm" error={errors.firstName} />
                      <Input label="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" required fullWidth size="sm" error={errors.lastName} />

                      {/* Locked email with explicit override */}
                      <div className="space-y-1">
                        <Input
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john.doe@company.com"
                          required
                          fullWidth
                          size="sm"
                          error={errors.email}
                          disabled={!emailUnlocked}
                          readOnly={!emailUnlocked}
                          className={emailUnlocked ? '' : 'bg-neutral-50 dark:bg-neutral-900/40 text-neutral-500'}
                          rightIcon={!emailUnlocked ? <Lock size={14} className="text-neutral-400" /> : null}
                        />
                        {!emailUnlocked ? (
                          <button type="button" onClick={() => setEmailUnlocked(true)} className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 inline-flex items-center gap-1 transition-colors">
                            <Pencil size={12} strokeWidth={1.75} /> Change email
                          </button>
                        ) : (
                          <p className="text-xs text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
                            <AlertTriangle size={12} strokeWidth={1.75} /> Changing the email will require re-verification on next login.
                          </p>
                        )}
                      </div>

                      {/* Phone with "+" prefix */}
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1.5">Phone Number</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm pointer-events-none">+</span>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="91 98765 43210"
                            className={`w-full pl-7 pr-3 py-2 text-sm rounded-xl bg-white dark:bg-neutral-900 ring-1 ring-inset ${errors.phone ? 'ring-rose-300 focus:ring-rose-500/40' : 'ring-neutral-200 dark:ring-neutral-800 focus:ring-primary-500/30'} text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 transition`}
                          />
                        </div>
                        {errors.phone && <p className="text-xs text-rose-600 dark:text-rose-400">{errors.phone}</p>}
                      </div>

                      <div className="md:col-span-2 xl:col-span-3">
                        <Input label="Job Title" name="jobTitle" value={formData.jobTitle} onChange={handleChange} placeholder="Training Manager" fullWidth size="sm" />
                      </div>

                      {/* Reset password row — for the contact's account */}
                      <div className="md:col-span-2 xl:col-span-3 rounded-xl bg-neutral-50/70 dark:bg-neutral-900/40 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 p-4 flex items-center gap-3">
                        <span className="inline-flex w-9 h-9 rounded-xl items-center justify-center bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700 text-neutral-600 dark:text-neutral-400 flex-shrink-0">
                          <KeyRound size={16} strokeWidth={1.75} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">Reset password</p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                            Send a password-reset email to {formData.email || 'this user'}.
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" type="button" onClick={handleSendPasswordReset} disabled={isResettingPassword || !formData.email}>
                          {isResettingPassword ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                          Send reset link
                        </Button>
                      </div>
                    </div>
                  </section>

                  {/* ── Section 3 ── */}
                  <section>
                    <SectionHeader Icon={Crown} title="Subscription & status" subtitle="Choose a plan and account status." />

                    <div className="space-y-2 mb-5">
                      {PLANS.map((plan) => {
                        const selected = formData.plan === plan.id;
                        return (
                          <button
                            type="button"
                            key={plan.id}
                            onClick={() => setFormData((prev) => ({ ...prev, plan: plan.id }))}
                            aria-pressed={selected}
                            className={`w-full text-left rounded-2xl bg-white dark:bg-neutral-900 p-4 cursor-pointer transition-all ${
                              selected
                                ? 'ring-2 ring-primary-500/60 bg-primary-50/40 dark:bg-primary-500/5 shadow-[0_4px_16px_-6px_rgba(255,138,0,0.25)]'
                                : 'ring-1 ring-inset ring-neutral-200 dark:ring-neutral-800 hover:ring-neutral-300 dark:hover:ring-neutral-700 hover:shadow-[0_4px_12px_-4px_rgba(16,24,40,0.06)]'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <span
                                aria-hidden="true"
                                className={`w-5 h-5 rounded-full ring-2 flex items-center justify-center flex-shrink-0 ${
                                  selected ? 'ring-primary-500' : 'ring-neutral-300 dark:ring-neutral-700'
                                }`}
                              >
                                {selected && <span className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                              </span>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{plan.name}</h3>
                                  {plan.popular && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-primary-100 text-primary-700 ring-1 ring-inset ring-primary-200 dark:bg-primary-500/15 dark:text-primary-300 dark:ring-primary-500/30">
                                      Popular
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{plan.desc}</p>
                                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                                  <FeatureChip Icon={Calendar} text={plan.features[0]} />
                                  <FeatureChip Icon={Users} text={plan.features[1]} />
                                  <FeatureChip Icon={GraduationCap} text={plan.features[2]} />
                                </div>
                              </div>

                              <div className="text-right flex-shrink-0">
                                <p className="text-base font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{plan.price}</p>
                                <p className="text-xs text-neutral-500 dark:text-neutral-400">{plan.period}</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Status chips */}
                    <div>
                      <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-2">
                        Status <span className="text-rose-500">*</span>
                      </label>
                      <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Status">
                        {STATUS_OPTIONS.map((opt) => {
                          const active = formData.status === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFormData((prev) => ({ ...prev, status: opt.value }))}
                              aria-pressed={active}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset transition-colors ${
                                active
                                  ? 'bg-primary-50 ring-primary-200 text-primary-700 dark:bg-primary-500/10 dark:ring-primary-500/30 dark:text-primary-300'
                                  : 'bg-white dark:bg-neutral-900 ring-neutral-200 dark:ring-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800/60'
                              }`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </section>

                  {/* ── Danger zone ── */}
                  <section>
                    <div className="flex items-start gap-3 mb-4">
                      <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-rose-50 ring-1 ring-inset ring-rose-100 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-500/20">
                        <AlertTriangle size={16} strokeWidth={1.75} />
                      </span>
                      <div>
                        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 leading-tight">Danger zone</h2>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">These actions can't be easily undone.</p>
                      </div>
                    </div>

                    <div className="rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-rose-200/70 dark:ring-rose-500/20 divide-y divide-rose-100/70 dark:divide-rose-500/10 overflow-hidden">
                      <div className="flex items-center justify-between gap-3 p-4">
                        <div>
                          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                            {isSuspended ? 'Reactivate organization' : 'Suspend organization'}
                          </p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 max-w-md">
                            {isSuspended
                              ? 'Restore access for all users in this organization.'
                              : 'Temporarily block all users in this org from logging in.'}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" type="button" onClick={handleToggleSuspend} className="ring-1 ring-inset ring-rose-200 text-rose-700 hover:bg-rose-50 dark:ring-rose-500/30 dark:text-rose-300 dark:hover:bg-rose-500/10">
                          {isSuspended ? 'Reactivate' : 'Suspend'}
                        </Button>
                      </div>

                      <div className="flex items-center justify-between gap-3 p-4">
                        <div>
                          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Delete organization</p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 max-w-md">
                            Permanently remove this organization and all of its sessions, trainers, and participants. Cannot be undone.
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" type="button" onClick={handleDelete} className="ring-1 ring-inset ring-rose-200 text-rose-700 hover:bg-rose-50 dark:ring-rose-500/30 dark:text-rose-300 dark:hover:bg-rose-500/10">
                          Delete
                        </Button>
                      </div>
                    </div>
                  </section>
                </>
              )}
            </div>

            {/* Sticky footer (hidden while loading) */}
            {!isLoading && (
              <div className="sticky bottom-0 z-10 bg-white/85 dark:bg-neutral-900/85 backdrop-blur-md border-t border-neutral-100 dark:border-neutral-800/70 px-6 py-3 flex items-center justify-between gap-3">
                <p className="text-xs hidden sm:flex items-center gap-1.5">
                  {isDirty ? (
                    <>
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                      <span className="text-amber-700 dark:text-amber-300">You have unsaved changes</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={12} className="text-emerald-600 dark:text-emerald-400" />
                      <span className="text-emerald-600 dark:text-emerald-400">All changes saved</span>
                    </>
                  )}
                </p>
                <div className="flex items-center gap-2 ml-auto">
                  <Button variant="ghost" type="button" onClick={handleCancel} disabled={isSubmitting}>
                    <X size={14} className="mr-1.5" /> Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!isDirty || isSubmitting}
                    className={isDirty ? 'shadow-[0_4px_12px_-4px_rgba(255,138,0,0.45)] ring-2 ring-primary-500/30' : ''}
                  >
                    {isSubmitting ? (
                      <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</>
                    ) : (
                      <><Save size={14} className="mr-1.5" /> Save changes</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </main>
      </div>

      {/* Unsaved changes confirm */}
      <ConfirmDialog
        isOpen={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        onConfirm={confirmLeave}
        title="Discard unsaved changes?"
        message="You've made changes that haven't been saved. If you leave now, those changes will be lost."
        confirmText="Discard changes"
        cancelText="Keep editing"
        variant="danger"
      />

      {/* Multiplexed danger-zone confirm */}
      <ConfirmDialog
        isOpen={dangerDialog.isOpen}
        onClose={closeDangerDialog}
        onConfirm={() => { dangerDialog.onConfirm?.(); }}
        title={dangerDialog.title}
        message={dangerDialog.message}
        confirmText={dangerDialog.confirmText}
        cancelText="Cancel"
        variant={dangerDialog.variant}
      />
    </DashboardLayout>
  );
};

// ────── small subcomponents (file-local) ──────

const SectionHeader = ({ Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-4">
    <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-800 text-neutral-600 dark:text-neutral-400">
      <Icon size={16} strokeWidth={1.75} />
    </span>
    <div>
      <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 leading-tight">{title}</h2>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>
    </div>
  </div>
);

const FeatureChip = ({ Icon, text }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-neutral-100 text-neutral-600 ring-1 ring-inset ring-neutral-200/70 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700">
    <Icon size={11} strokeWidth={1.75} />
    {text}
  </span>
);

// Skeleton placeholder shown inside the form card body while the
// record is loading. Section headers stay so the page feels structured;
// only the field grids become shimmer blocks.
const SkeletonForm = () => (
  <div className="space-y-8">
    {[Building2, UserSquare, Crown].map((Icon, idx) => (
      <section key={idx}>
        <SectionHeader Icon={Icon} title=" " subtitle=" " />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-10 rounded-xl bg-neutral-100 dark:bg-neutral-800 animate-pulse" />
          ))}
        </div>
      </section>
    ))}
  </div>
);

export default EditOrganizationPage;
